import React, { useState, useRef } from 'react';
import { usePlaid } from '@/contexts/PlaidContext';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { Loader2 } from 'lucide-react';

declare global {
  interface Window {
    Plaid: {
      create: (config: any) => any;
    };
  }
}

// Singleton for Plaid script loading
let plaidScriptPromise: Promise<void> | null = null;
let plaidScriptElement: HTMLScriptElement | null = null;

const loadPlaidScript = () => {
  if (plaidScriptPromise) return plaidScriptPromise;
  
  plaidScriptPromise = new Promise((resolve, reject) => {
    if (window.Plaid) {
      resolve();
      return;
    }

    plaidScriptElement = document.createElement('script');
    plaidScriptElement.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
    plaidScriptElement.async = true;
    plaidScriptElement.onload = () => resolve();
    plaidScriptElement.onerror = reject;
    document.body.appendChild(plaidScriptElement);
  });

  return plaidScriptPromise;
};

export const PlaidLinkButton: React.FC = () => {
  const { initializePlaidLink, handlePlaidSuccess, handlePlaidError } = usePlaid();
  const [linkHandler, setLinkHandler] = useState<any>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const { toast } = useToast();
  const mountedRef = useRef(true);

  const handleClick = async () => {
    if (linkHandler) {
      linkHandler.open();
      return;
    }

    try {
      setIsInitializing(true);
      console.log('Initializing Plaid Link...');
      
      await loadPlaidScript();
      if (!window.Plaid) {
        throw new Error('Plaid script failed to load');
      }

      const linkToken = await initializePlaidLink();
      console.log('Link token received successfully');

      const handler = window.Plaid.create({
        token: linkToken,
        onSuccess: (public_token: string, metadata: any) => {
          console.log('Plaid connection successful');
          handlePlaidSuccess({
            public_token,
            metadata
          });
          toast({
            title: "Success",
            description: "Bank account connected successfully",
          });
        },
        onExit: (err: any) => {
          if (err) {
            console.error('Plaid connection failed:', err.error_message);
            handlePlaidError(err);
            toast({
              title: "Error",
              description: err.error_message || "Failed to connect bank account",
              variant: "destructive",
            });
          } else {
            console.log('Plaid connection closed by user');
          }
        },
        onEvent: (eventName: string) => {
          console.log('Plaid event:', eventName);
        },
      });

      if (mountedRef.current) {
        console.log('Plaid handler created successfully');
        setLinkHandler(handler);
        handler.open();
      }
    } catch (error) {
      console.error('Error initializing Plaid:', error instanceof Error ? error.message : 'Unknown error');
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to initialize Plaid Link",
        variant: "destructive",
      });
    } finally {
      if (mountedRef.current) {
        setIsInitializing(false);
      }
    }
  };

  return (
    <Button
      onClick={handleClick}
      disabled={isInitializing}
      className="w-full"
    >
      {isInitializing ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Connecting...
        </>
      ) : (
        'Link New Account'
      )}
    </Button>
  );
}; 